import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Target, CalendarRange, Plus, Pencil, Trash2, ChevronLeft, ChevronRight, BarChart3, Users, ListChecks, Download } from 'lucide-react'
import { api, extractApiError, isPlanLimitError, isSessionEndedError } from '../../lib/api'
import { departmentList, goalsResponse } from '../../lib/types'
import { useMyWorkspaces } from '../../lib/useMyWorkspaces'
import { useAuthStore } from '../../stores/authStore'
import { isCompanyWideMember, canAssignGoals } from '../../lib/access'
import { Breadcrumb } from '../../components/Breadcrumb'
import { Button } from '../../components/ui/Button'
import { Select } from '../../components/ui/Select'
import { Avatar } from '../../components/Avatar'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { GoalFormModal, emptyGoalForm, goalToForm } from '../../components/GoalFormModal'
import { GoalAnalysisModal } from '../../components/GoalAnalysisModal'
import { FilterRow } from '../../components/filters/FilterRow'
import {
  HEALTH_LABEL,
  HEALTH_PILL,
  PRIORITY_LABEL,
  PRIORITY_PILL,
  fmtDate,
  addDays,
  monthShift,
  MONTHS,
  ProgressRing,
} from '../../lib/goals-ui.jsx'
import { formatDateTime } from '../../lib/format'
import { runExport } from '../../components/table/tableExport'

/** CSV/export columns for the Goals board (which renders as cards, not a
 *  DataTable, so it has no built-in export). Reuses the shared client-side
 *  `runExport` — no backend endpoint. Values mirror what the cards show. */
const GOAL_EXPORT_COLS = [
  { key: 'name', header: 'Goal' },
  { key: 'type', header: 'Type' },
  { key: 'owner', header: 'Owner', exportValue: (g) => g.ownerName || g.ownerId || '' },
  {
    key: 'priority',
    header: 'Priority',
    exportValue: (g) => PRIORITY_LABEL[g.priority] ?? g.priority ?? '',
  },
  {
    key: 'status',
    header: 'Status',
    exportValue: (g) => (g.manualStatus ?? '').replace(/_/g, ' ').trim(),
  },
  { key: 'health', header: 'Health', exportValue: (g) => HEALTH_LABEL[g.health] ?? g.health ?? '' },
  {
    key: 'progress',
    header: 'Progress %',
    exportValue: (g) => String(g.progress != null ? g.progress : g.completed ? 100 : 0),
  },
  { key: 'startDate', header: 'Start', exportValue: (g) => (g.startDate ? fmtDate(g.startDate) : '') },
  { key: 'targetDate', header: 'Target', exportValue: (g) => (g.targetDate ? fmtDate(g.targetDate) : '') },
  { key: 'tags', header: 'Tags', exportValue: (g) => (g.tags ?? []).join(', ') },
  { key: 'description', header: 'Description', exportValue: (g) => g.description ?? '' },
]

// The unified Goals page — Weekly + Monthly, one shared board driven by the
// `/goals` API. The active tab lives in the URL (`?tab=`) so it survives refresh
// and can be deep-linked.
const TABS = [
  { key: 'weekly', label: 'Weekly Goals', type: 'weekly', icon: Target },
  { key: 'monthly', label: 'Monthly Goals', type: 'monthly', icon: CalendarRange },
]

export function GoalsPage() {
  const [params, setParams] = useSearchParams()
  const requested = params.get('tab')
  const active = TABS.some((t) => t.key === requested) ? requested : 'weekly'
  const activeTab = TABS.find((t) => t.key === active) ?? TABS[0]

  return (
    <div>
      <Breadcrumb current="Goals" homePath="/app" homeLabel="Dashboard" />

      <div className="mb-5">
        <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)]">
          Goals
        </h1>
        <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
          Set weekly and monthly goals, track progress and keep your team aligned.
        </p>
      </div>

      <div
        role="tablist"
        aria-label="Goals"
        className="mb-5 flex gap-1 border-b border-[color:var(--color-rule)] overflow-x-auto overflow-y-hidden"
      >
        {TABS.map((t) => {
          const Icon = t.icon
          const on = t.key === active
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() =>
                setParams(
                  (p) => {
                    p.set('tab', t.key)
                    return p
                  },
                  { replace: true },
                )
              }
              className={
                'inline-flex items-center gap-2 whitespace-nowrap border-b-2 px-3.5 py-2.5 text-sm font-medium -mb-px cursor-pointer transition-colors ' +
                (on
                  ? 'border-[color:var(--color-accent-strong)] text-[color:var(--color-ink-strong)]'
                  : 'border-transparent text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:border-[color:var(--color-rule)]')
              }
            >
              <Icon size={15} />
              {t.label}
            </button>
          )
        })}
      </div>

      {/* Remount on tab switch so the board's period state resets cleanly. */}
      <GoalBoard key={activeTab.type} type={activeTab.type} />
    </div>
  )
}

/**
 * Shared Weekly/Monthly board. Lists the actor's goals for the viewed period,
 * each measurable via ClickUp-style key results. A goal's percent rolls up from
 * its key results (and sub-goals); goals with nothing measurable keep a manual
 * status. Clicking a goal opens its detail page.
 */
function GoalBoard({ type }) {
  const qc = useQueryClient()
  const me = useAuthStore((s) => s.user)
  const isMonthly = type === 'monthly'
  const [anchor, setAnchor] = useState(null) // null ⇒ current period
  const [modalOpen, setModalOpen] = useState(false)
  const [editingGoal, setEditingGoal] = useState(null) // null ⇒ create
  const [removeTarget, setRemoveTarget] = useState(null)
  const [analysisOpen, setAnalysisOpen] = useState(false)
  const [scope, setScope] = useState('involved') // 'involved' (All) | 'mine'
  const [healthFilter, setHealthFilter] = useState('all') // 'all' | health key
  // Custom date-range filter — both bounds must be set to apply. When active it
  // overrides the week/month period navigation (server lists goals overlapping
  // [from, to]).
  const [range, setRange] = useState({ from: '', to: '' })
  const rangeActive = Boolean(range.from && range.to)

  // Department filter — Company Admins / Sub Admins narrow the board to one
  // department's goals (owner belongs to it). Single-workspace: departments
  // come from the sole workspace. `''` = all departments.
  // Company Admins / Sub Admins / company-wide-access members (shared gate).
  const canFilterByDepartment = isCompanyWideMember(me)
  const [departmentId, setDepartmentId] = useState('')
  // Single-workspace: departments come from the sole workspace. Reuses the
  // shared `useMyWorkspaces` hook (correct endpoint + cached) rather than
  // re-rolling the query.
  const { workspaces } = useMyWorkspaces()
  const workspaceId = workspaces[0]?.id ?? null
  const departmentsQuery = useQuery({
    queryKey: ['departments', workspaceId],
    queryFn: async () => {
      const res = await api.get(`/workspaces/${workspaceId}/departments`)
      return departmentList.parse(res.data)
    },
    enabled: canFilterByDepartment && Boolean(workspaceId),
  })
  const departments = departmentsQuery.data?.data ?? []

  const listKey = ['goals', type, scope, departmentId, rangeActive ? `${range.from}:${range.to}` : anchor ?? 'current']
  const goalsQuery = useQuery({
    queryKey: listKey,
    queryFn: async () => {
      const qs = new URLSearchParams({ type, scope })
      if (departmentId) qs.set('departmentId', departmentId)
      if (rangeActive) {
        qs.set('from', range.from)
        qs.set('to', range.to)
      } else if (anchor) {
        qs.set('anchor', anchor)
      }
      const res = await api.get(`/goals?${qs.toString()}`)
      return goalsResponse.parse(res.data)
    },
  })

  const data = goalsQuery.data
  const goals = useMemo(() => data?.goals ?? [], [data])
  // Client-side status filter over the loaded list (composes with All/Mine).
  const visibleGoals = useMemo(
    () => (healthFilter === 'all' ? goals : goals.filter((g) => g.health === healthFilter)),
    [goals, healthFilter],
  )
  const total = data?.progress?.total ?? goals.length
  const completed = data?.progress?.completed ?? 0
  const periodStart = data?.periodStart ?? null
  const isCurrent = !anchor

  const invalidate = () => qc.invalidateQueries({ queryKey: ['goals', type] })

  const createMutation = useMutation({
    mutationFn: async (payload) =>
      (await api.post('/goals', { type, anchor: periodStart ?? undefined, ...payload })).data,
    onSuccess: () => {
      invalidate()
      toast.success('Goal added.')
      setModalOpen(false)
    },
    onError: (err) => {
      // The API interceptor already surfaces ONE amber "upgrade your plan"
      // warning (and its own dead-session toast), so suppress our duplicate.
      if (isPlanLimitError(err) || isSessionEndedError(err)) return
      toast.error(extractApiError(err).message)
    },
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, patch }) => (await api.patch(`/goals/${id}`, patch)).data,
    onSuccess: () => invalidate(),
    onError: (err) => {
      // The API interceptor already surfaces ONE amber "upgrade your plan"
      // warning (and its own dead-session toast), so suppress our duplicate.
      if (isPlanLimitError(err) || isSessionEndedError(err)) return
      toast.error(extractApiError(err).message)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id) => api.delete(`/goals/${id}`),
    onSuccess: () => {
      invalidate()
      toast.success('Goal deleted successfully.')
      setRemoveTarget(null)
    },
    onError: (err) => {
      // The API interceptor already surfaces ONE amber "upgrade your plan"
      // warning (and its own dead-session toast), so suppress our duplicate.
      if (isPlanLimitError(err) || isSessionEndedError(err)) return
      toast.error(extractApiError(err).message)
    },
  })

  const saving = createMutation.isPending || updateMutation.isPending

  function openAdd() {
    setEditingGoal(null)
    setModalOpen(true)
  }
  function openEdit(goal) {
    setEditingGoal(goal)
    setModalOpen(true)
  }
  function submitForm(payload) {
    if (editingGoal) {
      updateMutation.mutate(
        { id: editingGoal.id, patch: payload },
        {
          onSuccess: () => {
            invalidate()
            toast.success('Goal updated.')
            setModalOpen(false)
          },
        },
      )
    } else {
      createMutation.mutate(payload)
    }
  }
  const periodLabel = rangeActive
    ? `${fmtDate(range.from)} – ${fmtDate(range.to)}`
    : data
      ? isMonthly
        ? `${MONTHS[Number(periodStart.split('-')[1]) - 1]} ${periodStart.split('-')[0]}`
        : `${fmtDate(data.periodStart)} – ${fmtDate(data.periodEnd)}`
      : isMonthly
        ? 'Plan your goals for the month.'
        : 'Plan your goals for the working week.'

  const stepPrev = () => setAnchor(isMonthly ? monthShift(periodStart, -1) : addDays(periodStart, -7))
  const stepNext = () => setAnchor(isMonthly ? monthShift(periodStart, 1) : addDays(periodStart, 7))

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="mt-1 flex items-center gap-2 text-[color:var(--color-ink-muted)]">
          <button
            type="button"
            onClick={stepPrev}
            disabled={!periodStart || rangeActive}
            aria-label={isMonthly ? 'Previous month' : 'Previous week'}
            className="rounded p-1 hover:bg-[color:var(--color-card-hover)] hover:text-[color:var(--color-ink)] cursor-pointer disabled:opacity-40"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="text-sm">{periodLabel}</span>
          <button
            type="button"
            onClick={stepNext}
            disabled={!periodStart || rangeActive}
            aria-label={isMonthly ? 'Next month' : 'Next week'}
            className="rounded p-1 hover:bg-[color:var(--color-card-hover)] hover:text-[color:var(--color-ink)] cursor-pointer disabled:opacity-40"
          >
            <ChevronRight size={16} />
          </button>
          {!isCurrent ? (
            <button
              type="button"
              onClick={() => setAnchor(null)}
              className="ml-1 rounded-full border border-[color:var(--color-rule)] px-2 py-0.5 text-xs hover:bg-[color:var(--color-card-hover)] cursor-pointer"
            >
              {isMonthly ? 'This Month' : 'This Week'}
            </button>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Status filter — narrows the loaded list by goal health. "All
           *  statuses" is the reset; composes with the All/Mine scope. */}
          <Select
            sizeVariant="sm"
            fullWidth={false}
            value={healthFilter}
            onChange={(e) => setHealthFilter(e.target.value)}
            aria-label="Filter goals by status"
          >
            <option value="all">All statuses</option>
            <option value="not_started">Not Started</option>
            <option value="overdue">Overdue</option>
            <option value="due_today">Due today</option>
            <option value="in_progress">In Progress</option>
            <option value="on_hold">On Hold</option>
            <option value="completed">Completed</option>
            <option value="carried_forward">Carried Forward</option>
          </Select>
          {/* Clear the status filter once a specific status is picked — matches
           *  the "Clear range" affordance on the date filter. */}
          <FilterRow.Clear
            show={healthFilter !== 'all'}
            onClick={() => setHealthFilter('all')}
            label="Clear filter"
          />
          {/* Department filter — Company Admins / Sub Admins only. Narrows the
           *  board to one department's goals; "All Departments" is the reset. */}
          {canFilterByDepartment ? (
            <Select
              sizeVariant="sm"
              fullWidth={false}
              value={departmentId}
              onChange={(e) => setDepartmentId(e.target.value)}
              aria-label="Filter goals by department"
            >
              <option value="">All Departments</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </Select>
          ) : null}
          {/* All = everyone you can see (incl. your team's); Mine = owned by you. */}
          <div className="inline-flex rounded-full border border-[color:var(--color-rule)] p-0.5 text-sm">
            {[
              { key: 'involved', label: 'All' },
              { key: 'mine', label: 'Mine' },
            ].map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setScope(s.key)}
                className={
                  'rounded-full px-3 py-1 font-medium transition-colors cursor-pointer ' +
                  (scope === s.key
                    ? 'bg-[color:var(--color-accent-strong)] text-white'
                    : 'text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)]')
                }
              >
                {s.label}
              </button>
            ))}
          </div>
          <span className="rounded-full bg-[color:var(--color-accent-bg)] px-3 py-1 text-sm font-semibold text-[color:var(--color-accent-strong)]">
            {completed} / {total} completed
          </span>
          {visibleGoals.length > 0 ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                runExport('CSV', visibleGoals, GOAL_EXPORT_COLS, {
                  baseName: 'goals',
                  title: 'Goals',
                  isoDate: new Date().toISOString().slice(0, 10),
                })
              }
              title={`Export ${visibleGoals.length} goal${visibleGoals.length === 1 ? '' : 's'} to CSV`}
            >
              <Download size={15} />
              Export
            </Button>
          ) : null}
          <button
            type="button"
            onClick={() => setAnalysisOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-[color:var(--color-rule)] px-3 py-1.5 text-sm text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
          >
            <BarChart3 size={15} />
            Analysis
          </button>
          <Button onClick={openAdd}>
            <Plus size={15} />
            Add Goal
          </Button>
        </div>
      </div>

      {/* Date-range filter — set BOTH dates to list goals overlapping the
       *  range (overrides the week/month navigation above). */}
      <FilterRow secondary>
        <FilterRow.DateField
          label="From"
          value={range.from}
          onChange={(v) => setRange((r) => ({ ...r, from: v }))}
          max={range.to || undefined}
        />
        <FilterRow.DateField
          label="To"
          value={range.to}
          onChange={(v) => setRange((r) => ({ ...r, to: v }))}
          min={range.from || undefined}
        />
        {(range.from || range.to) && !rangeActive ? (
          <span className="text-xs text-[color:var(--color-ink-muted)]">
            Set both dates to apply the range.
          </span>
        ) : null}
        <FilterRow.Spacer />
        <FilterRow.Clear
          show={Boolean(range.from || range.to)}
          onClick={() => setRange({ from: '', to: '' })}
          label="Clear range"
          boxed
        />
      </FilterRow>

      {/* Body */}
      {goalsQuery.isLoading ? (
        <p className="text-sm text-[color:var(--color-ink-muted)]">Loading your goals…</p>
      ) : goalsQuery.isError ? (
        <p className="text-sm text-[color:var(--color-priority-urgent)]">{extractApiError(goalsQuery.error).message}</p>
      ) : goals.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-12 text-center">
          <Target size={28} className="mx-auto mb-2 text-[color:var(--color-ink-subtle)]" />
          <p className="text-sm font-medium text-[color:var(--color-ink-strong)]">
            No {isMonthly ? 'monthly' : 'weekly'} goals yet.
          </p>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            Set goals for the {isMonthly ? 'month' : 'week'} and add measurable key results to track them.
          </p>
          <div className="mt-4">
            <Button onClick={openAdd}>
              <Plus size={15} />
              Add Your First Goal
            </Button>
          </div>
        </div>
      ) : visibleGoals.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-12 text-center">
          <Target size={28} className="mx-auto mb-2 text-[color:var(--color-ink-subtle)]" />
          <p className="text-sm font-medium text-[color:var(--color-ink-strong)]">
            No goals match this filter.
          </p>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            No {HEALTH_LABEL[healthFilter]?.toLowerCase() ?? ''} goals in this period. Try a different status.
          </p>
          <div className="mt-4">
            <button
              type="button"
              onClick={() => setHealthFilter('all')}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[color:var(--color-rule)] px-3 py-1.5 text-sm text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
            >
              Clear Filter
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {visibleGoals.map((goal) => {
            const displayPct = goal.progress != null ? goal.progress : goal.completed ? 100 : 0
            const priority = goal.priority ?? 'normal'
            const tags = goal.tags ?? []
            const linked = goal.linkedProgress // { done, total } | null
            const followerCount = (goal.followerIds ?? []).length
            const dueLabel = goal.targetDate ? fmtDate(goal.targetDate) : null
            return (
              <div
                key={goal.id}
                className="group relative flex flex-col overflow-hidden rounded-2xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] transition-shadow hover:shadow-md"
              >
                {/* Top accent — matches the sidebar navy for a unified chrome. */}
                <div className="h-1.5 w-full bg-[color:var(--color-primary)]" />

                {/* Hover actions (edit / delete) — only for members who can
                 *  actually manage this goal (owner / creator / supervisor /
                 *  admin). Backend `canManageGoalDefinition` enforces the same
                 *  rule (an assigned owner who isn't the creator can't edit or
                 *  delete), so hiding the affordance here just stops a 403. */}
                {goal.canEdit ? (
                  <div className="absolute right-2 top-3 z-10 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                    <button
                      type="button"
                      onClick={() => openEdit(goal)}
                      title="Edit"
                      aria-label={`Edit ${goal.name}`}
                      className="rounded-md bg-[color:var(--color-card)]/80 p-1.5 text-[color:var(--color-ink-muted)] shadow-sm hover:bg-[color:var(--color-card-hover)] hover:text-[color:var(--color-ink)] cursor-pointer"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setRemoveTarget(goal)}
                      title="Delete"
                      aria-label={`Delete ${goal.name}`}
                      className="rounded-md bg-[color:var(--color-card)]/80 p-1.5 text-[color:var(--color-ink-muted)] shadow-sm hover:bg-[color:var(--color-card-hover)] hover:text-[color:var(--color-priority-urgent)] cursor-pointer"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ) : null}

                {/* Body — the whole thing links to the detail page */}
                <Link
                  to={`/app/goals/${goal.id}`}
                  className="flex flex-1 flex-col items-center px-5 pb-3 pt-6 text-center"
                >
                  <ProgressRing percent={displayPct} size={76} stroke={7} />
                  {/* w-full + overflow-wrap:anywhere so a long unbroken name
                   *  (e.g. "ppppp…") breaks inside the card instead of
                   *  stretching it and overlapping neighbouring content. */}
                  <div className="mt-3 line-clamp-2 w-full break-words [overflow-wrap:anywhere] font-semibold text-[color:var(--color-ink-strong)]">
                    {goal.name}
                  </div>
                  {goal.description ? (
                    <p className="mt-1 line-clamp-2 w-full break-words [overflow-wrap:anywhere] text-xs text-[color:var(--color-ink-muted)]">
                      {goal.description}
                    </p>
                  ) : null}
                  {/* Priority + health at a glance */}
                  <div className="mt-2 flex flex-wrap items-center justify-center gap-1.5">
                    <span
                      className={
                        'rounded-full px-2 py-0.5 text-[11px] font-semibold ' +
                        (PRIORITY_PILL[priority] ?? '')
                      }
                    >
                      {PRIORITY_LABEL[priority] ?? priority}
                    </span>
                    {goal.health ? (
                      <span
                        className={
                          'rounded-full px-2 py-0.5 text-[11px] font-semibold ' +
                          (HEALTH_PILL[goal.health] ?? '')
                        }
                      >
                        {HEALTH_LABEL[goal.health] ?? goal.health}
                      </span>
                    ) : null}
                  </div>
                  {/* Tags */}
                  {tags.length ? (
                    <div className="mt-2 flex flex-wrap items-center justify-center gap-1">
                      {tags.slice(0, 3).map((tag) => (
                        <span
                          key={tag}
                          className="max-w-[8rem] truncate rounded-full bg-[color:var(--color-card-hover)] px-2 py-0.5 text-[10px] text-[color:var(--color-ink-muted)]"
                        >
                          {tag}
                        </span>
                      ))}
                      {tags.length > 3 ? (
                        <span className="text-[10px] text-[color:var(--color-ink-subtle)]">
                          +{tags.length - 3}
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                  {/* Compact meta — due date, linked tasks, followers */}
                  <div className="mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[12px] text-[color:var(--color-ink-subtle)]">
                    {dueLabel ? (
                      <span className="inline-flex items-center gap-1" title="Due date">
                        <CalendarRange size={12} />
                        Due {dueLabel}
                      </span>
                    ) : null}
                    {linked && linked.total > 0 ? (
                      <span className="inline-flex items-center gap-1" title="Linked tasks completed">
                        <ListChecks size={12} />
                        {linked.done}/{linked.total} tasks
                      </span>
                    ) : null}
                    {followerCount > 0 ? (
                      <span className="inline-flex items-center gap-1" title="Followers">
                        <Users size={12} />
                        {followerCount}
                      </span>
                    ) : null}
                  </div>
                </Link>

                {/* Footer — owner avatar + created date/time */}
                <div className="flex items-center justify-between gap-2 border-t border-[color:var(--color-rule)] px-4 py-3">
                  <div className="flex min-w-0 items-center gap-2">
                    <Avatar name={goal.ownerName ?? me?.fullName ?? ''} size="sm" colorByName tooltip />
                    <div className="min-w-0 leading-tight">
                      {/* Owner name always shown — for the viewer's own goals too. */}
                      <div className="truncate text-sm font-medium text-[color:var(--color-ink)]">
                        {goal.ownerName ?? me?.fullName ?? '—'}
                      </div>
                      {goal.createdById && String(goal.createdById) !== String(goal.ownerId) ? (
                        <div className="truncate text-xs text-[color:var(--color-ink-subtle)]">
                          by {goal.createdByName ?? '—'}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <span className="shrink-0 text-xs text-[color:var(--color-ink-subtle)]">
                    {formatDateTime(goal.createdAt)}
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      )}

      <GoalFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={submitForm}
        saving={saving}
        title={editingGoal ? 'Edit Goal' : 'Add Goal'}
        submitLabel={editingGoal ? 'Save Goal' : 'Add Goal'}
        isEdit={Boolean(editingGoal)}
        // Creating on the CURRENT-period board is valid even after the working
        // week has ended (e.g. on a weekend) — mirrors the server's period-based
        // guard so the client doesn't reject a goal the API would accept.
        isCurrentPeriod={isCurrent}
        // Managers / Admins get the create-time Assignee picker (department →
        // person); everyone else creates goals owned by themselves.
        canAssign={canAssignGoals(me)}
        type={editingGoal?.type ?? type}
        initial={
          editingGoal
            ? goalToForm(editingGoal)
            : emptyGoalForm({ startDate: data?.periodStart ?? '', targetDate: data?.periodEnd ?? '' })
        }
      />

      <GoalAnalysisModal
        open={analysisOpen}
        onClose={() => setAnalysisOpen(false)}
        type={type}
        anchor={periodStart}
        scope={scope}
        periodLabel={periodLabel}
      />

      <ConfirmDialog
        open={Boolean(removeTarget)}
        title="Delete Goal?"
        message={removeTarget ? `"${removeTarget.name}" will be removed.` : ''}
        confirmLabel="Delete"
        variant="danger"
        confirming={deleteMutation.isPending}
        onCancel={() => setRemoveTarget(null)}
        onConfirm={() => removeTarget && deleteMutation.mutate(removeTarget.id)}
      />
    </div>
  )
}
